import { useMiniCartStore } from '@/components/MiniCart/use-new-minicart-store';
import { useCartContext } from '@/context/cart';
import { ADD_PRODUCT_TO_CART } from '@/hooks/ProductFullDetail/productFullDetail.gql';
import { useEnsureCartId } from '@/hooks/use-ensure-cart-id';
import { useIsMobile } from '@/hooks/use-is-mobile';
import { pushEcommerceEvent } from '@/lib/data-layer';
import { deriveErrorMessage } from '@/util/deriveErrorMessage';
import { generateUrl } from '@/util/imageUtils';
import { useMutation } from '@apollo/client';
import { useMemo, useState } from 'react';

const track = ({ product, quantity }: { product: any; quantity: number }) => {
  try {
    const currency =
      product?.price_range?.minimum_price?.final_price?.currency ||
      product?.price_range?.maximum_price?.final_price?.currency;
    const price =
      product?.price_range?.minimum_price?.final_price?.value ||
      product?.price_range?.maximum_price?.final_price?.value;

    if (!currency || typeof price !== 'number') {
      return;
    }

    let categoryParams: Record<string, string> = {};
    if (product?.categories?.length) {
      const firstItem = product.categories[0];
      const crumbs = firstItem.breadcrumbs
        ? [...firstItem.breadcrumbs].sort((a: any, b: any) => a.category_level - b.category_level).map((c: any) => c.category_name)
        : [];
      if (firstItem.name) {
        crumbs.push(firstItem.name);
      }

      crumbs.forEach((name, index) => {
        if (index === 0) {
          categoryParams['item_category'] = name;
        } else if (index < 5) {
          categoryParams[`item_category${index + 1}`] = name;
        }
      });
    }

    pushEcommerceEvent({
      event: 'add_to_cart',
      ecommerce: {
        currency: currency,
        value: price * quantity,
        items: [
          {
            item_name: product.name,
            item_id: product.sku,
            price: price,
            quantity: quantity,
            ...categoryParams,
          },
        ],
        sku: product.sku,
      },
    });

    window.pintrk &&
      window.pintrk('track', 'addtocart', {
        value: price * quantity,
        order_quantity: quantity,
        currency: currency,
        line_items: [
          {
            product_name: product.name,
            product_id: product.uid,
            product_category: categoryParams['item_category'] || null,
            product_price: price,
            product_brand: 'hisea',
          },
        ],
      });

    // fbq
    window.fbq &&
      window.fbq('track', 'AddToCart', {
        content_ids: [product.sku],
        content_name: product.name,
        content_type: 'product',
        contents: [
          {
            product_name: product.name,
            product_id: product.uid,
            product_category: categoryParams['item_category'] || null,
            product_price: price,
            product_brand: 'hisea',
          },
        ],
        currency: currency,
        value: price * quantity,
      });
    // Amazon Pixel - Purchase Event
    const eventData = {
      value: price * quantity,
      currency: currency,
      unitsSold: quantity,
      contentId: product.sku,
      name: product.name,
      size: null,
    };
    // if (window.amzn) {
    //   console.log('window.amzn', eventData);
    //   window.amzn('trackEvent', 'AddToCart', eventData);
    // }
  } catch (error) {
    console.error(error);
  }
};

const getProductImage = ({ mediaGalleryEntries }: { mediaGalleryEntries: any }) => {
  let product_image = '';

  let media = mediaGalleryEntries && mediaGalleryEntries[0];

  // 默认展示图片position最小的
  mediaGalleryEntries &&
    mediaGalleryEntries.forEach((item: any, index: number) => {
      if (item.position < media.position) {
        media = item;
      }
    });

  product_image =
    process.env.NEXT_PUBLIC_MAGENTO_BACKEND_URL +
    generateUrl(mediaGalleryEntries && media?.file, 'image-product')(90, 132);

  return {
    product_image,
  };
};

export const useAddToCart = ({
  product,
  mediaGalleryEntries,
  selectedOptionsArray,
}: {
  product: any;
  mediaGalleryEntries: any;
  selectedOptionsArray: any;
}) => {
  const [{ cartId }] = useCartContext();

  const { isMobile } = useIsMobile();

  const setCartStatus = useMiniCartStore(state => state?.setCartStatus);

  const [addProductToCart, { error: errorAddingProductToCart, loading: isAddProductLoading }] =
    useMutation(ADD_PRODUCT_TO_CART);
  const [isCreatingCart, setIsCreatingCart] = useState(false);
  const [failedToCart, setFailedToCart] = useState(false);
  const { handleEnsureCartId } = useEnsureCartId();

  const handleAddToCart = async ({ quantity }: { quantity: number }) => {
    const { product_image } = getProductImage({
      mediaGalleryEntries,
    });

    setIsCreatingCart(true);
    setFailedToCart(false);
    const nextCartId = await handleEnsureCartId();
    setIsCreatingCart(false);

    if (!nextCartId) {
      setFailedToCart(true);
      return;
    }

    let variables = {};

    let categoryIdToPass = 0;
    let categoryNameToPass = '';
    if (product?.categories?.length) {
      const firstItem = product.categories[0];
      categoryNameToPass = firstItem.name || '';
      if (firstItem.uid) {
        try {
          const decodedUid = typeof window !== 'undefined' ? atob(firstItem.uid) : Buffer.from(firstItem.uid, 'base64').toString('utf8');
          categoryIdToPass = parseInt(decodedUid.replace(/\D/g, '')) || 0;
        } catch (e) {
          console.error(e);
        }
      }
    }

    variables = {
      cartId: nextCartId,
      product: {
        sku: product.sku,
        quantity,
        product_image: product_image,
        selected_options: selectedOptionsArray,
        category_id: categoryIdToPass,
        category_name: categoryNameToPass,
      },
      entered_options: [
        {
          uid: product.uid,
          value: product.name,
        },
      ],
    };
    console.log('variables', variables);

    try {
      await addProductToCart({
        variables,
      });

      if (product) {
        track({
          product,
          quantity,
        });
      }

      setCartStatus('addtocart');
    } catch (error) {
      console.error(error);
    }
  };

  const derivedErrorMessage = useMemo(() => deriveErrorMessage([errorAddingProductToCart]), [errorAddingProductToCart]);

  return {
    handleAddToCart,
    isAdding: isAddProductLoading || isCreatingCart,
    errorMessage: derivedErrorMessage,
    failedToCart,
  };
};
